import { isEmpty } from 'lodash'
import { apiMakeChainServicesPpiProxyCalls, apiPostPpiProxy } from './Api'
import { VNPPI_SERVICES } from './Enums'
import { toast } from 'react-toastify'
import { swapCaseFirstLetter } from '../VNUtil/Strings'
import { calculateDistributionOfPayment, vn_getEntries } from '../VNUtil/VNUtils'
import { centsToDollars } from '../utils/formatters'

/**
 * Given tenderType, get all details of the tender as configured in VNConfigs
 * @param {ImmutableMap} ppiConfigurations - Returned by VNAPI PPI /configurations and injected into Redux immutable state.
 * @param {String} tenderType - The specific tender you want to retrieve configs for. Available: QRPAY_TENDER, TICKET_TENDER, GIFTCARD_TENDER
 * @returns {Object} - Returns the configs if found, null otherwise. Example: { integration: FANMAKER, services: ['getAmenities'] }
 */
export const getTender = (ppiConfigurations, tenderType) => {
  if (!ppiConfigurations.isEmpty()) {
    const tenders = ppiConfigurations.get('tenders')
    if (tenders && tenders[tenderType]) {
      return tenders[tenderType]
    }
  }
  
  return null
}

/**
 * Given a paymentType, get all details of the tender as configured in VNConfigs
 * @param {ImmutableMap} ppiConfigurations - Returned by VNAPI PPI /configurations and injected into Redux immutable state.
 * @param {String} paymentType - Available: vnapi_ticket, vnapi_giftcard, vnapi_qrpay
 * @returns {Object} - Returns the configs if found, null otherwise. Example: { integration: FANMAKER, services: ['getAmenities'] }
 */
export const getTenderByPaymentType = (ppiConfigurations, paymentType) => {

  if (!ppiConfigurations.isEmpty()) {

    const tenders = ppiConfigurations.get('tenders')

    const strippedPaymentType = paymentType.split('_')
    const tenderType = `${strippedPaymentType[1].toUpperCase()}_TENDER`

    if (tenders && tenders[tenderType]) {
      return tenders[tenderType]
    }
  }
  return null
}

/**
 * Checks if the payment type is a valid PPI type.
 * @param {String} paymentType - Available: vnapi_ticket, vnapi_giftcard, vnapi_qrpay
 * @returns {Boolean} Returns true if it is, false otherwise.
 */
export const paymentTypeIsPpi = (paymentType) => {
  switch(paymentType) {
    case 'vnapi_ticket':
    case 'vnapi_qr':
    case 'vnapi_gift':
      return true
  }
  return false
}

/**
 * Helper function to extrapolate error message and toast...ing.
 * @param {Object} response - The body response from the API call
 * @param {Function} errorFunc - The setError function to call
 * @return {Boolean} Returns true if an error was handled, false otherwise
 */
const handleError = (response, errorFunc) => {
  if (response instanceof Error) {
    if (errorFunc) {
      errorFunc(true)
    }
    toast.error(response.message)
    return true
  }
  return false
}

/**
 * Handle an array of responses from VNAPI PPI to determine what we should do within POS
 * @param {Object} ppiTender - From getTender above, all the details of a tender as configured in VNConfigs - Example: { integration: FANMAKER, services: ['getAmenities'] }
 * @param {Array} responses - An array of responses from each API call to VNAPI proxy in order, this is the body response.
 * @param {Object} posInfo - The POS Information about this current POS - Format: { userId: '', vendorName: '', venueId: '' }
 * @param {Object} externalDataAndFunctionality - Functionality and data that is external to this Utils, required format: 
 * { addPaymentToOrderInProgress: <FUNCTION>, balanceDueInCents: 3202, updateOrderInProgress: <FUNCTION>, token: '<TOKEN>', paymentType: 'vnapi_ticket', tenderDislayName: 'Ticket' }
 */
export const handleServiceResponses = (ppiTender, responses, posInfo, externalDataAndFunctionality, order) => {

  // do we need to redirect after we are complete?
  let redirectEnabled = false

  // the total balance that is remaining after all the calls, used for redirects
  let remainingBalance = 0

  for (let i = 0; i < ppiTender.services.length; i++) {

    // We want these separated out so that you can combine services together. 
    // Some may have the same execution code, like GET_BALANCE & GET_CONTEXTUAL_BALANCE and they can't be combo'ed
    // since that wouldn't make any sense

    if (ppiTender.services[i] === VNPPI_SERVICES.GET_CONTEXTUAL_DISCOUNTS) {

      if (handleError(responses[i], externalDataAndFunctionality.setError)) {
        return
      }

      // Expectation
      // {
      //   "discounts": [{
      //     "sku": "1234554321",
      //     "promotion": "BRAVES_1025",
      //     "amount": 3938
      //      "originalPricePerItem": 1000,
      //      "quantity": 3
      //   }, {
      //      "sku": "1122334455",
      //      "promotion": "BRAVES_1025",
      //       "amount": 500,
      //      "originalPricePerItem": 1000,
      //      "quantity": 1
      //   }]
      // }

      let updatedOrder = {}
      Object.assign(updatedOrder, order)
      
      let totalDiscountAmount = 0

      responses[i].discounts.forEach(discount => {

        // save off and calculate the total discount being applied
        totalDiscountAmount += discount.amount

        let distributionArray = []

        // if quantity is greater than 1, we need to distribute
        if (discount.quantity > 1) {
          distributionArray = calculateDistributionOfPayment(discount.amount, order.amountInCents, discount.quantity)
        }

        for (let j = 0; j < updatedOrder.itemModels.length; j++) {
          
          const itemModel = updatedOrder.itemModels[j]
          
          if (itemModel.variant?.productSku === discount.sku) {

            if (distributionArray.length > 0) {
              itemModel.priceInCents -= distributionArray.pop()
            } else {
              itemModel.priceInCents -= discount.amount
            }

            itemModel.price = centsToDollars(itemModel.priceInCents)
            
          }
        }
      })

      updatedOrder.balanceDueInCents -= totalDiscountAmount
      updatedOrder.amountInCents -= totalDiscountAmount
      updatedOrder.subtotalAmountInCents -= totalDiscountAmount

      // need to update this local param state so we can use it on subsequent checks. 
      // The next set of checks can't use redux state.
      externalDataAndFunctionality.balanceDueInCents = updatedOrder.balanceDueInCents

      remainingBalance = updatedOrder.balanceDueInCents

      toast.success('Discount successfully applied.')

      externalDataAndFunctionality.updateOrderInProgress(updatedOrder)

      if (remainingBalance > 0 && externalDataAndFunctionality.redirect) {
        redirectEnabled = true
      }

    }

    if (ppiTender.services[i] === VNPPI_SERVICES.GET_BALANCE ||
        ppiTender.services[i] === VNPPI_SERVICES.GET_CONTEXTUAL_BALANCE) {
      
      if (handleError(responses[i], externalDataAndFunctionality.setError)) {
        return
      }

      const balanceInCents = Math.ceil(responses[i].balance * 100)

      let tenderAmountInCents
      let remainingTicketBalance

      if (balanceInCents >= externalDataAndFunctionality.balanceDueInCents) {
        tenderAmountInCents = externalDataAndFunctionality.balanceDueInCents
        remainingTicketBalance = balanceInCents - externalDataAndFunctionality.balanceDueInCents
      } else {
        tenderAmountInCents = balanceInCents
        remainingTicketBalance = 0
      }

      externalDataAndFunctionality.addPaymentToOrderInProgress({
        tenderAmountInCents,
        paymentType: externalDataAndFunctionality.paymentType,
        tenderDisplayName: externalDataAndFunctionality.tenderDisplayName,
        token: externalDataAndFunctionality.token,
        remainingTicketBalance,
        userAttributes: {
          posInfo: posInfo
        }
      })

      remainingBalance = externalDataAndFunctionality.balanceDueInCents - balanceInCents

      if (remainingBalance > 0 && externalDataAndFunctionality.redirect) {
        redirectEnabled = true
      }

    }

    // handle the final redirect if necessary
    if (redirectEnabled) {
      externalDataAndFunctionality.redirect(remainingBalance)
    }
    
  }
}

/**
 * Format the order from internal state to what VNAPI needs.
 * @param {Object} order - The current order for the user, from state getOrderInProgress(state)
 * @returns {Object} - Returns a formatted orderInfo object that VNAPI expects.
 */
export const formatOrder = (order) => {

  let cart = []
  let orderInfo = {}

  if (order) {

    if (order.itemModels) {
      order.itemModels.forEach(itemModel => {
        let item = {
          quantity: itemModel.quantity,
          id: itemModel.id,
          sku: itemModel.variant.productSku,
          itemName: itemModel.name,
          itemCost: itemModel.priceInCents / 100,
          type: itemModel.category
        }

        cart.push(item)
      })
    }

    orderInfo = {
      orderId: order.uuid, // we want this to be unique globally, and out actual orderNumber is not
      orderUuid: order.uuid,
      cart: cart,
      total: order.amountInCents / 100,
      subTotal: order.subtotalAmountInCents / 100,
      currency: "USD"
    }
  }

  return orderInfo
}

/**
 * 
 * @param {Object} order - The current order for the user, from state getOrderInProgress(state)
 * @param {ImmutableMap} ppiConfigurations - Returned by VNAPI PPI /configurations and injected into Redux immutable state.
 * @param {} paymentTransactions - 
 * @param {Object} posInfo - The POS Information about this current POS - Format: { userId: '', vendorName: '', venueId: '' }
 */
export const handlePpiReportOrder = (order, ppiConfigurations, paymentTransactions, posInfo) => {

  let needToReportOrder = {}
  let payments = []
  
  // loop through payments to see if we need to support
  // sending the reportOrder to VNAPI PPI
  if (paymentTransactions) {

    paymentTransactions.map((paymentTransaction, index) => {
      
      // we only want to submit the data once per integration
      if (paymentTypeIsPpi(paymentTransaction.paymentTypeName)) {
        
        const tender = getTenderByPaymentType(ppiConfigurations, paymentTransaction.paymentTypeName)

        // ensure that only 1 of each integration is tagged for inclusion for reporting
        if (tender?.integration) {
          needToReportOrder[tender.integration] = tender.integration
        }

        let payment = {
          spent: paymentTransaction.creditCardDetails.spent,
          credentialInfo: {
            credential: paymentTransaction.creditCardDetails.paymentId,
          }
        }

        // This is required due to the POS axios response transforming everything!
        // it turns FANMAKER for an example into fANMAKER as the key!!! 
        // ay caramba!!!! Mi cabeza!
        const integrationCapitalized = swapCaseFirstLetter(tender?.integration)

        if (paymentTransaction.creditCardDetails[integrationCapitalized]) {
          payment[tender.integration] = {
            ...paymentTransaction.creditCardDetails[integrationCapitalized]
          }
        }
        
        payments.push(payment)
      }
    })
  }

  if (!isEmpty(needToReportOrder)) {
    let transaction = {
      orderInfo: formatOrder(order),
      posInfo: posInfo
    }
  
    transaction.orderInfo.payments = payments
  
    // report an order for each integration type
    for (const [integration, _] of vn_getEntries(needToReportOrder)) {
      apiPostPpiProxy(VNPPI_SERVICES.REPORT_ORDER, integration, transaction)
    }
  } else {
    console.log('PPI - Nothing to reportOrder...')
  }
}

/**
 * Generate the specific transaction object that is sent to VNAPI for the PPI proxy, this contextual specific to each service.
 * @param {Array} services - All services that have been being used. Example: getBalance, getContextualBalance
 * @param {String} credentialValue - The value that is being checked with PPI
 * @param {Object} order - The current order for the user, from state getOrderInProgress(state)
 * @param {Object} posInfo - The POS Information about this current POS - Format: { userId: '', vendorName: '', venueId: '' }
 * @returns {Object keyed by service} - The object will contain keys for each service enabled with the matching transaction needed to make the API call.
 */
export const generateTransactions = (services, credentialValue, order, posInfo) => {
  
  let transactions = {}

  services.forEach(service => {
  
    let baseTransaction = {}

    if (credentialValue) {
      baseTransaction = {
        credentialInfo: {
          credential: credentialValue
        },
        posInfo: posInfo
      }
    }

    let orderInfo

    switch(service) {
      case VNPPI_SERVICES.GET_BALANCE:
        // Nothing to do atm
        break
      case VNPPI_SERVICES.GET_CONTEXTUAL_BALANCE:
        orderInfo = formatOrder(order)

        baseTransaction = {
          ...baseTransaction,
          orderInfo
        }

        if (order) {
          baseTransaction.amount = order.amountInCents / 100
        }

        break
      case VNPPI_SERVICES.GET_CONTEXTUAL_DISCOUNTS:
        orderInfo = formatOrder(order)

        baseTransaction = {
          ...baseTransaction,
          orderInfo
        }

        break
    }

    transactions[service] = baseTransaction

  })

  return transactions
}

/**
 * Use this to dynamically generate the appropriate transactions and make all the ppi proxy calls in order and gather
 * responses. This is handy to use in one go instead of having to manage and track each individually. VOID Function, returns nothing.
 * @param {Object} ppiTender - From getTender above, all the details of a tender as configured in VNConfigs - Example: { integration: FANMAKER, services: ['getAmenities'] }
 * @param {Object} order - The current order for the user, from state getOrderInProgress(state)
 * @param {Object} posInfo - The POS Information about this current POS - Format: { userId: '', vendorName: '', venueId: '' }
 * @param {Object} externalDataAndFunctionality - Functionality and data that is external to this Utils, required format: 
 * { addPaymentToOrderInProgress: <FUNCTION>, balanceDueInCents: 3202, updateOrderInProgress: <FUNCTION>, token: '<TOKEN>', paymentType: 'vnapi_ticket', tenderDislayName: 'Ticket' }
 */
export const handleFullPpiFlow = async (ppiTender, order, posInfo, externalDataAndFunctionality) => {

  const transactions = generateTransactions(ppiTender.services, externalDataAndFunctionality.token, order, posInfo)

  const responses = await apiMakeChainServicesPpiProxyCalls(ppiTender.services, ppiTender.integration, transactions)
  
  handleServiceResponses(ppiTender, responses, posInfo, externalDataAndFunctionality, order)
}

/**
 * Used to determine if the order in Stadium has a PPI payment type in it
 * @param {Object} orderTransaction - Response from Stadium /pos/order
 * @returns {Boolean} - Returns true if the order has a PPI payment type, false otherwise
 */
export const isOrderPpi = (orderTransaction) => {
  if (orderTransaction && orderTransaction.paymentTransactions) {
    for (let i = 0; i < orderTransaction.paymentTransactions.length; i++) {
      if (paymentTypeIsPpi(orderTransaction.paymentTransactions[i].paymentTypeName)) {
        return true
      }
    }
  }
  return false
}